import { PropertyFieldReadOnlyViewModel } from "../types/property-field-read-only.view-model";
import { PropertyFieldValueViewModel } from "../types/property-field-value.view-model";
import {
  PropertySectionKind,
  PropertySectionViewModel,
} from "../types/property-section.view-model";
import { getKeyOfProperty } from "./get-key-of-property";
import { getKeyOfPropertyUnit } from "./get-key-of-property-unit";

function createSecurityUnitFields(
  keyOfSecurity: number,
  keyOfUnit: number
): PropertyFieldReadOnlyViewModel[] {
  return [
    {
      data_key: getKeyOfPropertyUnit("unit_number", keyOfSecurity, keyOfUnit),
      type: "text",
      label: `Unit Name`,
    },
    {
      data_key: getKeyOfPropertyUnit("unit_value", keyOfSecurity, keyOfUnit),
      type: "currency",
      label: "Unit Value",
    },
    {
      data_key: getKeyOfPropertyUnit("unit_square_feet", keyOfSecurity, keyOfUnit),
      type: "number",
      label: "Unit Square Feet",
    },
    {
      data_key: getKeyOfPropertyUnit("unit_sold", keyOfSecurity, keyOfUnit),
      type: "boolean",
      label: "Unit Sold",
    },
  ];
}

function createSecurityFields(keyOfSecurity: number): PropertyFieldReadOnlyViewModel[] {
  return [
    {
      data_key: getKeyOfProperty("postcode", keyOfSecurity),
      type: "text",
      label: "Postcode",
    },
    {
      data_key: getKeyOfProperty("title_number", keyOfSecurity),
      type: "text",
      label: "Title Number",
    },
    {
      data_key: getKeyOfProperty("property_type", keyOfSecurity),
      type: "dropdown",
      label: "Property Type",
    },
    {
      data_key: getKeyOfProperty("sub_type", keyOfSecurity),
      type: "dropdown",
      label: "Property Sub-Type",
    },
    {
      data_key: getKeyOfProperty("development", keyOfSecurity),
      type: "boolean",
      label: "Development",
    },
    {
      data_key: getKeyOfProperty("development_complete", keyOfSecurity),
      type: "boolean",
      label: "Is Development Complete?",
    },
    {
      data_key: getKeyOfProperty("completion_date", keyOfSecurity),
      type: "date",
      label: "Anticipated Practical Completion Date",
    },
    {
      data_key: getKeyOfProperty("purchase_price", keyOfSecurity),
      type: "currency",
      label: "Purchase Price",
    },
    {
      data_key: getKeyOfProperty("recent_valuation", keyOfSecurity),
      type: "currency",
      label: "Most Recent Valuation",
    },
    {
      data_key: getKeyOfProperty("gdv", keyOfSecurity),
      type: "currency",
      label: "GDV (if applicable)",
    },
    {
      data_key: getKeyOfProperty("draws_to_date", keyOfSecurity),
      type: "currency",
      label: "Draws to Date",
    },
    {
      data_key: getKeyOfProperty("remaining_to_draw", keyOfSecurity),
      type: "currency",
      label: "Remaining to Draw",
    },
    {
      data_key: getKeyOfProperty("insurance_expiry", keyOfSecurity),
      type: "date",
      label: "Insurance Expiry Date",
    },
    {
      data_key: getKeyOfProperty("square_feet", keyOfSecurity),
      type: "number",
      label: "Square Feet",
    },
    {
      data_key: getKeyOfProperty("number_of_units", keyOfSecurity),
      type: "number",
      label: "Number of Units",
    },
  ];
}

export function createDefaultSections(
  values: PropertyFieldValueViewModel[]
): PropertySectionViewModel[] {
  const sections: PropertySectionViewModel[] = [
    {
      kind: PropertySectionKind.Section,
      title: "Property Details",
      fields: [],
    },
  ];

  createPropertyFields(0);

  sections.push({
    kind: PropertySectionKind.Section,
    title: "Securities",
    fields: [
      {
        data_key: "security",
        type: "number",
        label: "Quantity of Security",
      },
    ],
  });

  const numberOfSecurity = values.find(value => value.data_key === "security")?.value;

  if (numberOfSecurity) {
    Array.from({ length: numberOfSecurity }).forEach((_, index) => {
      const key = index + 1;
      createPropertyFields(key);
    });
  }

  return sections;

  function createPropertyFields(key: number) {
    const fieldsOfSecurity: PropertyFieldReadOnlyViewModel[] = [];

    fieldsOfSecurity.push(...createSecurityFields(key));

    if (key === 0) {
      sections[0].fields.push(...fieldsOfSecurity);
    } else {
      sections.push({
        kind: PropertySectionKind.Security,
        title: `Security ${key}`,
        fields: fieldsOfSecurity,
      });
    }

    const numberOfUnits = values.find(
      value => value.data_key === getKeyOfProperty("number_of_units", key)
    )?.value;

    if (numberOfUnits) {
      Array.from({ length: numberOfUnits }).forEach((_, index) => {
        const fieldsOfUnits: PropertyFieldReadOnlyViewModel[] = [];

        const unitCount = index + 1;
        fieldsOfUnits.push(...createSecurityUnitFields(key, unitCount));

        sections.push({
          kind: PropertySectionKind.Unit,
          title: `Unit ${unitCount}`,
          fields: fieldsOfUnits,
        });
      });
    }
  }
}

export function createSectionsFromFormData(
  formData: Record<string, any>
): PropertySectionViewModel[] {
  const sections: PropertySectionViewModel[] = [
    {
      kind: PropertySectionKind.Section,
      title: "Property Details",
      fields: [],
    },
  ];

  createPropertyFields(0);

  sections.push({
    kind: PropertySectionKind.Section,
    title: "Securities",
    fields: [
      {
        data_key: "security",
        type: "number",
        label: "Security",
      },
    ],
  });

  const numberOfSecurity = formData.security;

  if (numberOfSecurity) {
    Array.from({ length: numberOfSecurity }).forEach((_, index) => {
      const key = index + 1;
      createPropertyFields(key);
    });
  }

  return sections;

  function createPropertyFields(key: number) {
    const fieldsOfSecurity: PropertyFieldReadOnlyViewModel[] = [];

    fieldsOfSecurity.push(...createSecurityFields(key));

    if (key === 0) {
      sections[0].fields.push(...fieldsOfSecurity);
    } else {
      sections.push({
        kind: PropertySectionKind.Security,
        title: `Security ${key}`,
        fields: fieldsOfSecurity,
      });
    }

    const numberOfUnits = formData[getKeyOfProperty("number_of_units", key)];

    if (numberOfUnits) {
      Array.from({ length: numberOfUnits }).forEach((_, index) => {
        const fieldsOfUnits: PropertyFieldReadOnlyViewModel[] = [];

        const unitCount = index + 1;
        fieldsOfUnits.push(...createSecurityUnitFields(key, unitCount));

        sections.push({
          kind: PropertySectionKind.Unit,
          title: `Unit ${unitCount}`,
          fields: fieldsOfUnits,
        });
      });
    }
  }
}
